// Device-side catalog edit mutations (P1-18 Phase 2).
//
// Split from data/catalog.ts to keep that file under the 400-line
// per-file guard. The read-path + photo endpoints stay in catalog.ts;
// anything that writes an edit column lives here.
//
// Rule 1 shape: every mutation sends idempotency_key + expected_version.
// device_id / staff_id come from the JWT the server already peeks (via
// peekSessionClaims in db-tx middleware), so they don't go in the body.

import type {
  AmmoAttributesInput,
  HolsterAttributesInput,
  OpticsAttributesInput,
} from "@betterpos/domain";
import { apiBase } from "./catalog.ts";
import type { AmmoAttributes, HolsterAttributes, OpticsAttributes } from "./catalog-attrs.ts";

export type EditResult<T> =
  | { ok: true; value: T }
  | {
      ok: false;
      status: number;
      error: string;
      // 409 only — the server's current version so the UI can refetch and
      // retry; present only when error === "version_conflict".
      currentVersion?: number;
      // 400 only — the field that failed validation + the machine-readable
      // reason (empty_after_trim | too_long | too_many | not_array | ...).
      field?: string;
      reason?: string;
    };

export type TagOverrideUpdate = {
  id: string;
  version: number;
  tagNameOverride: string | null;
};
export type FirearmFeaturesUpdate = {
  itemId: string;
  version: number;
  features: string[] | null;
};

// Idempotency keys are UUIDs minted on the device per rule 1. The native
// `crypto.randomUUID()` returns a v4; v7 (time-sortable) would be nicer
// for debugging but requires a new dep. The server attributes writes
// from the JWT (device_id/staff_id), not the body, so sending just the
// key is enough for the audit trail here. The dedicated server-side
// idempotency store is deferred to a later phase; expected_version
// alone makes duplicate submits safe (first wins, second → 409).
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export async function updateItemTagOverride(
  token: string,
  itemId: string,
  input: { expectedVersion: number; tagNameOverride: string | null },
): Promise<EditResult<TagOverrideUpdate>> {
  const res = await fetch(`${apiBase()}/catalog/items/${encodeURIComponent(itemId)}`, {
    method: "PATCH",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({
      expected_version: input.expectedVersion,
      tag_name_override: input.tagNameOverride,
      idempotency_key: newIdempotencyKey(),
    }),
  });
  if (!res.ok) return decodeEditError(res);
  const body = (await res.json()) as { item: TagOverrideUpdate };
  return { ok: true, value: body.item };
}

export async function updateFirearmFeatures(
  token: string,
  itemId: string,
  input: { expectedVersion: number; features: string[] | null },
): Promise<EditResult<FirearmFeaturesUpdate>> {
  const res = await fetch(`${apiBase()}/catalog/items/${encodeURIComponent(itemId)}/firearm`, {
    method: "PATCH",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({
      expected_version: input.expectedVersion,
      features: input.features,
      idempotency_key: newIdempotencyKey(),
    }),
  });
  if (!res.ok) return decodeEditError(res);
  const body = (await res.json()) as { firearmAttributes: FirearmFeaturesUpdate };
  return { ok: true, value: body.firearmAttributes };
}

// --- Per-kind attribute edits (P1-18 PR B) -------------------------------

export type AmmoAttributesUpdate = AmmoAttributes & { itemId: string };
export type OpticsAttributesUpdate = OpticsAttributes & { itemId: string };
export type HolsterAttributesUpdate = HolsterAttributes & { itemId: string };

async function patchKind<U>(
  token: string,
  path: string,
  itemId: string,
  expectedVersion: number,
  attributes: unknown,
  responseKey: "ammoAttributes" | "opticsAttributes" | "holsterAttributes",
): Promise<EditResult<U>> {
  const res = await fetch(`${apiBase()}/catalog/items/${encodeURIComponent(itemId)}/${path}`, {
    method: "PATCH",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({
      expected_version: expectedVersion,
      attributes,
      idempotency_key: newIdempotencyKey(),
    }),
  });
  if (!res.ok) return decodeEditError(res);
  const body = (await res.json()) as Record<string, U>;
  // responseKey always keys into body because the server contract
  // guarantees it; cast suppresses the Record-index optional-return.
  return { ok: true, value: body[responseKey] as U };
}

export function updateItemAmmo(
  token: string,
  itemId: string,
  input: { expectedVersion: number; attributes: AmmoAttributesInput },
): Promise<EditResult<AmmoAttributesUpdate>> {
  return patchKind<AmmoAttributesUpdate>(
    token,
    "ammo",
    itemId,
    input.expectedVersion,
    input.attributes,
    "ammoAttributes",
  );
}

export function updateItemOptics(
  token: string,
  itemId: string,
  input: { expectedVersion: number; attributes: OpticsAttributesInput },
): Promise<EditResult<OpticsAttributesUpdate>> {
  return patchKind<OpticsAttributesUpdate>(
    token,
    "optics",
    itemId,
    input.expectedVersion,
    input.attributes,
    "opticsAttributes",
  );
}

export function updateItemHolster(
  token: string,
  itemId: string,
  input: { expectedVersion: number; attributes: HolsterAttributesInput },
): Promise<EditResult<HolsterAttributesUpdate>> {
  return patchKind<HolsterAttributesUpdate>(
    token,
    "holster",
    itemId,
    input.expectedVersion,
    input.attributes,
    "holsterAttributes",
  );
}

// --- Features autocomplete (P1-18 Phase 3) --------------------------------

export type FeatureSuggestion = { value: string; count: number };
export type FeatureSuggestionsResult =
  | { ok: true; suggestions: FeatureSuggestion[] }
  | { ok: false; status: number; error: string };

/**
 * Fetches catalog-wide feature suggestions for the chip-editor dropdown.
 * Returns an empty-but-ok list on any API error so the caller can keep
 * showing recent-use suggestions and never surfaces a UI failure for an
 * optional-polish feature.
 */
export async function fetchFeatureSuggestions(
  token: string,
  q: string = "",
  limit: number = 10,
): Promise<FeatureSuggestionsResult> {
  const url = new URL(`${apiBase()}/catalog/features/suggest`);
  if (q.length > 0) url.searchParams.set("q", q);
  if (limit !== 10) url.searchParams.set("limit", String(limit));
  let res: Response;
  try {
    res = await fetch(url.toString(), {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return { ok: false, status: 0, error: "network_error" };
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: "unknown" }))) as { error?: string };
    return { ok: false, status: res.status, error: body.error ?? "unknown" };
  }
  const body = (await res.json()) as { suggestions: FeatureSuggestion[] };
  return { ok: true, suggestions: body.suggestions };
}

async function decodeEditError<T>(res: Response): Promise<EditResult<T>> {
  const body = (await res.json().catch(() => ({ error: "unknown" }))) as {
    error?: string;
    currentVersion?: number;
    field?: string;
    reason?: string;
  };
  // Build the object a key at a time so exactOptionalPropertyTypes doesn't
  // complain about explicitly-undefined properties.
  const out: EditResult<T> & { ok: false } = {
    ok: false,
    status: res.status,
    error: body.error ?? "unknown",
  };
  if (body.currentVersion !== undefined) out.currentVersion = body.currentVersion;
  if (body.field !== undefined) out.field = body.field;
  if (body.reason !== undefined) out.reason = body.reason;
  return out;
}
